import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Field, TextArea, TagsField, ToggleRow, Skeleton } from "@/components/admin/Field";
import ImageUploadField from "@/components/admin/ImageUploadField";

function slugify(value) {
  return (value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const MOCK_KINDS = [
  "dashboard",
  "analytics",
  "table",
  "ai",
  "pipeline",
  "trading",
  "booking",
  "shop",
  "website",
];

const EMPTY = {
  title: "",
  slug: "",
  short_description: "",
  full_description: "",
  category: "",
  tags: [],
  technologies: [],
  client: "",
  year: new Date().getFullYear().toString(),
  live_url: "",
  cover_image: "",
  gallery_images: [],
  case_study: "",
  tagline: "",
  challenge: "",
  solution: "",
  outcome: [],
  services: [],
  mock: "dashboard",
  screens: [],
  accent: "#E4FF00",
  featured: false,
  published: true,
  sort_order: 0,
};

export default function ProjectEditPage() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api
      .get(`/projects/${id}`)
      .then(({ data }) => setForm({ ...EMPTY, ...data, tags: data.tags || [], technologies: data.technologies || [], gallery_images: data.gallery_images || [], outcome: data.outcome || [], services: data.services || [], screens: data.screens || [], mock: data.mock || "dashboard" }))
      .catch(() => toast.error("Project not found"))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function updateTitle(v) {
    update("title", v);
    if (!slugTouched && isNew) {
      update("slug", slugify(v));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!form.slug.trim()) update("slug", slugify(form.title));
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug || slugify(form.title) };
      const sortOrderNum = Number(payload.sort_order);
      payload.sort_order = Number.isFinite(sortOrderNum) ? sortOrderNum : 0;
      if (isNew) {
        const { data } = await api.post("/admin/projects", payload);
        toast.success("Project created");
        navigate(`/admin/portfolio/${data.id}`, { replace: true });
      } else {
        const { data } = await api.put(`/admin/projects/${id}`, payload);
        setForm({ ...EMPTY, ...data });
        toast.success("Saved");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  function addGalleryImage(url) {
    if (!url) return;
    update("gallery_images", [...(form.gallery_images || []), url]);
  }
  function removeGalleryImage(idx) {
    update("gallery_images", (form.gallery_images || []).filter((_, i) => i !== idx));
  }

  if (loading) {
    return (
      <div className="px-6 py-8 md:px-10 md:py-12">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="mt-4 h-72" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="px-6 py-8 md:px-10 md:py-12">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link
            to="/admin/portfolio"
            className="inline-flex items-center gap-1.5 font-body text-xs text-white/45 hover:text-white"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to projects
          </Link>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-white md:text-4xl">
            {isNew ? "New project" : form.title || "Untitled project"}
          </h1>
          {!isNew && (
            <p className="mt-1 font-body text-xs text-white/45">/{form.slug}</p>
          )}
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="project-save-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#0055ff] px-5 py-2.5 font-body text-sm font-medium text-white transition hover:bg-[#1a66ff] disabled:opacity-60"
        >
          <Save className="h-4 w-4" />
          {saving ? "Saving…" : isNew ? "Create project" : "Save changes"}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Section title="Basics">
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Title" value={form.title} onChange={updateTitle} placeholder="Lumen — Editorial commerce" testid="project-title" />
              <Field
                label="Slug"
                value={form.slug}
                onChange={(v) => {
                  setSlugTouched(true);
                  update("slug", v);
                }}
                placeholder="lumen-editorial-commerce"
                testid="project-slug"
                hint="Used in the public URL: /portfolio/{slug}"
              />
              <Field label="Category" value={form.category} onChange={(v) => update("category", v)} placeholder="E-commerce" />
              <Field label="Client" value={form.client} onChange={(v) => update("client", v)} placeholder="Lumen Maison" />
              <Field label="Year" value={form.year} onChange={(v) => update("year", v)} placeholder="2025" />
              <Field label="Live URL" value={form.live_url} onChange={(v) => update("live_url", v)} placeholder="https://…" />
            </div>
          </Section>

          <Section title="Story">
            <TextArea
              label="Short description"
              value={form.short_description}
              onChange={(v) => update("short_description", v)}
              placeholder="One-line summary that appears on cards"
              rows={2}
              testid="project-short-desc"
            />
            <TextArea
              label="Full description"
              value={form.full_description}
              onChange={(v) => update("full_description", v)}
              rows={4}
              testid="project-full-desc"
            />
            <TextArea
              label="Case study"
              value={form.case_study}
              onChange={(v) => update("case_study", v)}
              rows={8}
              hint="Longer narrative shown on the project detail page."
              testid="project-case-study"
            />
          </Section>

          <Section title="Case study — public site">
            <p className="-mt-1 mb-1 font-body text-[11px] leading-relaxed text-white/40">
              These fields drive the homepage “Selected Work” section and the public
              /work/{form.slug || "slug"} case-study page.
            </p>
            <Field
              label="Tagline"
              value={form.tagline}
              onChange={(v) => update("tagline", v)}
              placeholder="Revenue intelligence for modern finance teams."
              testid="project-tagline"
              hint="Big one-line statement under the project name."
            />
            <TextArea
              label="The challenge"
              value={form.challenge}
              onChange={(v) => update("challenge", v)}
              rows={4}
              placeholder="What problem did the client face?"
              testid="project-challenge"
            />
            <TextArea
              label="The solution"
              value={form.solution}
              onChange={(v) => update("solution", v)}
              rows={4}
              placeholder="What did we design / build?"
              testid="project-solution"
            />
            <OutcomeEditor value={form.outcome} onChange={(v) => update("outcome", v)} />
            <TagsField
              label="Services"
              value={form.services}
              onChange={(v) => update("services", v)}
              testid="project-services"
              hint="e.g. Product design, Platform engineering, Automation"
            />
            <KindSelect
              label="Card visual"
              value={form.mock}
              onChange={(v) => update("mock", v)}
              hint="Animated mockup shown on the project card."
            />
            <ScreensField value={form.screens} onChange={(v) => update("screens", v)} />
          </Section>

          <Section title="Taxonomy">
            <TagsField label="Tags" value={form.tags} onChange={(v) => update("tags", v)} testid="project-tags" />
            <TagsField label="Technologies" value={form.technologies} onChange={(v) => update("technologies", v)} testid="project-technologies" />
          </Section>

          <Section title="Gallery">
            <ImageUploadField
              label="Add gallery image"
              value=""
              onChange={(url) => addGalleryImage(url)}
              testid="project-gallery-upload"
              helper="Each upload is appended to the gallery below."
            />
            {form.gallery_images?.length > 0 && (
              <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
                {form.gallery_images.map((url, idx) => (
                  <div key={idx} className="group relative overflow-hidden rounded-2xl border border-white/10">
                    <img src={url} alt="" className="h-32 w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(idx)}
                      className="absolute right-2 top-2 rounded-full bg-black/70 px-2 py-1 font-body text-[10px] text-white opacity-0 transition group-hover:opacity-100"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>

        <div className="space-y-6">
          <Section title="Cover">
            <ImageUploadField
              label="Cover image"
              value={form.cover_image}
              onChange={(v) => update("cover_image", v)}
              testid="project-cover"
            />
          </Section>

          <Section title="Visibility">
            <div className="flex flex-col gap-3">
              <ToggleRow
                label="Published"
                description="Visible on the public portfolio."
                value={form.published}
                onChange={(v) => update("published", v)}
                testid="project-toggle-published"
              />
              <ToggleRow
                label="Featured"
                description="Appears on the homepage Selected Work section."
                value={form.featured}
                onChange={(v) => update("featured", v)}
                testid="project-toggle-featured"
              />
              <Field
                label="Sort order"
                value={form.sort_order}
                onChange={(v) => update("sort_order", v)}
                placeholder="0"
                hint="Lower numbers appear first."
              />
            </div>
          </Section>
        </div>
      </div>
    </form>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-3xl border border-white/[0.07] bg-white/[0.015] p-6 md:p-7">
      <h2 className="mb-5 font-display text-base font-medium tracking-tight text-white/90">{title}</h2>
      <div className="flex flex-col gap-5">{children}</div>
    </section>
  );
}

const SELECT_CLS =
  "w-full rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 font-body text-sm text-white transition focus:border-[#0055ff]/50 focus:bg-white/[0.04] focus:outline-none";
const LABEL_CLS =
  "mb-2 block font-body text-[10px] tracking-[0.3em] uppercase text-white/45";

function KindSelect({ label, value, onChange, hint }) {
  return (
    <div>
      <label className={LABEL_CLS}>{label}</label>
      <select value={value || "dashboard"} onChange={(e) => onChange(e.target.value)} className={SELECT_CLS}>
        {MOCK_KINDS.map((k) => (
          <option key={k} value={k} className="bg-[#0a0a0a] text-white">
            {k}
          </option>
        ))}
      </select>
      {hint && <p className="mt-1.5 font-body text-[11px] text-white/40">{hint}</p>}
    </div>
  );
}

// Ordered list of mockup screens shown on the detail page (first = hero visual).
function ScreensField({ value = [], onChange }) {
  const items = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("dashboard");
  return (
    <div>
      <label className={LABEL_CLS}>Detail screens (in order)</label>
      <div className="mb-2 flex flex-wrap gap-2">
        {items.length === 0 && (
          <span className="font-body text-[11px] text-white/35">No screens yet — add at least one.</span>
        )}
        {items.map((s, idx) => (
          <span
            key={idx}
            className="inline-flex items-center gap-1 rounded-full border border-white/12 bg-white/[0.05] px-3 py-1 font-body text-xs text-white/85"
          >
            <span className="text-white/40">{idx + 1}.</span> {s}
            <button
              type="button"
              onClick={() => onChange(items.filter((_, i) => i !== idx))}
              className="text-white/40 hover:text-white"
              aria-label={`remove ${s}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <select value={draft} onChange={(e) => setDraft(e.target.value)} className={SELECT_CLS}>
          {MOCK_KINDS.map((k) => (
            <option key={k} value={k} className="bg-[#0a0a0a] text-white">
              {k}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => onChange([...items, draft])}
          className="shrink-0 rounded-xl border border-white/12 bg-white/[0.04] px-4 font-body text-sm text-white/85 transition hover:border-white/30"
        >
          Add
        </button>
      </div>
      <p className="mt-1.5 font-body text-[11px] text-white/40">
        The first screen is the large hero visual; the rest fill the gallery.
      </p>
    </div>
  );
}

// Editable list of result metrics ({ k: label, v: value }).
function OutcomeEditor({ value = [], onChange }) {
  const items = Array.isArray(value) ? value : [];
  const setItem = (i, key, val) =>
    onChange(items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
  return (
    <div>
      <label className={LABEL_CLS}>Outcome metrics</label>
      <div className="flex flex-col gap-2.5">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={it.v ?? ""}
              onChange={(e) => setItem(i, "v", e.target.value)}
              placeholder="+212%"
              className="w-1/3 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 font-body text-sm text-white placeholder-white/30 focus:border-[#0055ff]/50 focus:outline-none"
            />
            <input
              value={it.k ?? ""}
              onChange={(e) => setItem(i, "k", e.target.value)}
              placeholder="Qualified meetings"
              className="flex-1 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 font-body text-sm text-white placeholder-white/30 focus:border-[#0055ff]/50 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => onChange(items.filter((_, idx) => idx !== i))}
              className="shrink-0 rounded-xl border border-white/12 px-3 font-body text-sm text-white/50 transition hover:border-white/30 hover:text-white"
              aria-label="remove metric"
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onChange([...items, { v: "", k: "" }])}
        className="mt-2.5 rounded-xl border border-white/12 bg-white/[0.04] px-4 py-2 font-body text-sm text-white/85 transition hover:border-white/30"
      >
        + Add metric
      </button>
      <p className="mt-1.5 font-body text-[11px] text-white/40">
        Big value (left) + its label (right). Shown as the 3-up results grid.
      </p>
    </div>
  );
}
